import { typedLocalStorage } from "helpers/typedLocalStorage";
import { SortDirection } from "services/shared-models/sharedEnums";
import { create } from "zustand";

type StoreState = {
  modelFilterVisible: boolean;
  modelFilterPined: boolean;
  generalSearch: string;
  sortState: Record<string, SortDirection>;
  pagination: {
    currentPage: number;
    totalPages: number;
  };
  filters: Record<string, any>;
};

type StoreActions = {
  toggleModelFilterVisible: () => void;
  toggleModelFilterPin: () => void;
  setGeneralSearch: (search: string) => void;
  toggleFieldSortState: (field: string, dir: SortDirection | null) => void;
  getSortRequestObj: () => { field: string; dir: SortDirection }[];
  setPagination: (pagination: { currentPage?: number; totalPages?: number }) => void;
  setFilters: (filters: Record<string, any>) => void;
  setFilter: (field: string, value: any) => void;
  reset: () => void;
};

const initialState = (): StoreState => ({
  modelFilterVisible: typedLocalStorage.getItem<boolean>("modelFilterPined") ?? false,
  modelFilterPined: typedLocalStorage.getItem<boolean>("modelFilterPined") ?? false,
  generalSearch: "",
  sortState: {},
  pagination: {
    currentPage: 1,
    totalPages: 1,
  },
  filters: {},
});

const useGridStore = create<StoreState & StoreActions>((set, get) => ({
  ...initialState(),
  toggleModelFilterVisible: () => {
    set({
      modelFilterVisible: !get().modelFilterVisible,
      modelFilterPined: false,
    });
    typedLocalStorage.setItem("modelFilterPined", false);
  },
  toggleModelFilterPin: () => {
    const current = get().modelFilterPined;
    set({ modelFilterPined: !current });
    typedLocalStorage.setItem("modelFilterPined", !current);
  },
  setGeneralSearch: (search) => set({ generalSearch: search }),
  toggleFieldSortState: (field, dir) => {
    if (dir === null) {
      const newState = { ...get().sortState };
      delete newState[field];
      set({ sortState: newState });
    } else {
      set({ sortState: { ...get().sortState, [field]: dir } });
    }
  },
  getSortRequestObj: () => {
    const sortState = get().sortState;
    return Object.entries(sortState).map(([field, dir]) => ({ field, dir }));
  },
  setPagination: (pagination) =>
    set({
      pagination: {
        currentPage: pagination.currentPage ?? get().pagination.currentPage,
        totalPages: pagination.totalPages ?? get().pagination.totalPages,
      },
    }),
  setFilters: (filters) => set({ filters }),
  setFilter: (field, value) => set({ filters: { ...get().filters, [field]: value } }),
  reset: () => set(initialState()),
}));

export default useGridStore;
